/**
 * DebugHelper.js
 * -------------------------------
 * Centralized debugging and logging utility for the contact system.
 * Provides contextual logging, error tracking, and diagnostic information.
 *
 * Features:
 * - Contextual logging with timestamps and module identification
 * - Button and health check diagnostics
 * - Instance and global debug information
 * - SSR-safe operation with fallback behaviors
 */

class DebugHelper {
    static createContext(moduleName) {
        return {
            moduleName,
            info: (message, data) => DebugHelper.info(`[${moduleName}] ${message}`, data),
            debug: (message, data) => DebugHelper.debug(`[${moduleName}] ${message}`, data),
            warn: (message, data) => DebugHelper.warn(`[${moduleName}] ${message}`, data),
            error: (message, data) => DebugHelper.error(`[${moduleName}] ${message}`, data),
            success: (message, data) => DebugHelper.success(`[${moduleName}] ${message}`, data)
        };
    }

    static log(level, message, data = null) {
        const timestamp = new Date().toISOString();
        const prefix = `🌐 [${level}] ${timestamp}`;
        
        if (data) {
            console.log(`${prefix} ${message}`, data);
        } else {
            console.log(`${prefix} ${message}`);
        }
    }

    static info(message, data = null) {
        this.log('DebugHelper', `ℹ️ ${message}`, data);
    }

    static debug(message, data = null) {
        this.log('DebugHelper', `🔧 ${message}`, data);
    }

    static warn(message, data = null) {
        this.log('DebugHelper', `⚠️ ${message}`, data);
    }

    static error(message, data = null) {
        this.log('DebugHelper', `❌ ${message}`, data);
    }

    static success(message, data = null) {
        this.log('DebugHelper', `✅ ${message}`, data);
    }

    /**
     * Log button details for debugging
     * @param {NodeList|Array} buttons Contact buttons found on page
     */
    static logButtonDetails(buttons) {
        try {
            if (!buttons || buttons.length === 0) {
                this.warn('No contact buttons found on page');
                return;
            }

            this.debug(`Found ${buttons.length} contact button(s):`);
            
            Array.from(buttons).forEach((button, index) => {
                const details = {
                    index: index + 1,
                    id: button.id || 'no-id',
                    className: button.className || 'no-class',
                    text: button.textContent?.trim() || 'no-text',
                    dataset: button.dataset || {},
                    tagName: button.tagName?.toLowerCase() || 'unknown'
                };
                
                this.debug(`Button ${index + 1}:`, details);
            });
        } catch (error) {
            this.error('Failed to log button details:', error);
        }
    }

    /**
     * Log health check results
     * @param {Object} health Health check object from namespace
     */
    static logHealthCheck(health) {
        try {
            if (!health) {
                this.warn('Health check object is null or undefined');
                return;
            }

            this.debug('System health check:', {
                initialized: health.initialized || false,
                components: health.components || {},
                modalReady: health.modalReady || false,
                errors: health.errors || {},
                timestamp: new Date().toISOString()
            });

            // Log component status
            if (health.components) {
                const componentStatus = Object.entries(health.components)
                    .map(([name, status]) => `${name}: ${status ? '✅' : '❌'}`)
                    .join(', ');
                
                this.debug(`Components: ${componentStatus}`);
            }

            // Log error summary
            if (health.errors && health.errors.totalErrors > 0) {
                this.warn(`Health check found ${health.errors.totalErrors} error(s), ${health.errors.fatalErrors || 0} fatal`);
            } else {
                this.success('Health check passed - no errors detected');
            }
        } catch (error) {
            this.error('Failed to log health check:', error);
        }
    }

    /**
     * Log configuration details
     * @param {Object} config Configuration object
     */
    static logConfig(config) {
        try {
            if (!config) {
                this.debug('No configuration provided');
                return;
            }

            this.debug('Applying configuration:', {
                type: config.type || 'none',
                messagePlaceholder: config.messagePlaceholder || 'none',
                prefillMessage: config.prefillMessage || 'none',
                checkboxOptions: config.checkboxOptions || [],
                timestamp: new Date().toISOString()
            });
        } catch (error) {
            this.error('Failed to log config:', error);
        }
    }

    /**
     * Get instance debug information
     * @param {Object} instance Contact trigger instance
     * @param {Object} globalListener Global contact listener
     * @returns {Object} Debug information
     */
    static getInstanceDebugInfo(instance, globalListener) {
        try {
            return {
                instance: {
                    initialized: instance?.initialized || false,
                    hasNamespace: !!instance?.namespace,
                    hasClickHandler: !!instance?.clickHandler,
                    isGlobalInstance: instance?.constructor?.hasGlobalInstance?.() || false
                },
                globalListener: {
                    active: globalListener?.isActive?.() || false,
                    processing: globalListener?.getProcessingState?.() || false,
                    debugInfo: globalListener?.getDebugInfo?.() || {}
                },
                timestamp: new Date().toISOString()
            };
        } catch (error) {
            this.error('Failed to get instance debug info:', error);
            return {
                error: error.message,
                timestamp: new Date().toISOString()
            };
        }
    }

    /**
     * Get global debug information
     * @param {Function} ContactTriggerClass Contact trigger class
     * @param {Object} globalListener Global contact listener
     * @returns {Object} Global debug information
     */
    static getGlobalDebugInfo(ContactTriggerClass, globalListener) {
        try {
            return {
                global: {
                    hasGlobalInstance: ContactTriggerClass?.hasGlobalInstance?.() || false,
                    globalInstance: ContactTriggerClass?.getGlobalInstance?.() || null
                },
                listener: globalListener?.getDebugInfo?.() || {},
                environment: {
                    isBrowser: typeof window !== 'undefined',
                    hasDocument: typeof document !== 'undefined',
                    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown'
                },
                timestamp: new Date().toISOString()
            };
        } catch (error) {
            this.error('Failed to get global debug info:', error);
            return {
                error: error.message,
                timestamp: new Date().toISOString()
            };
        }
    }

    /**
     * Create a formatted error report
     * @param {Error} error Error object
     * @param {Object} context Additional context
     * @returns {Object} Formatted error report
     */
    static createErrorReport(error, context = {}) {
        try {
            return {
                message: error.message || 'Unknown error',
                stack: error.stack || 'No stack trace',
                name: error.name || 'Error',
                context,
                timestamp: new Date().toISOString(),
                userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
                url: typeof window !== 'undefined' ? window.location.href : 'unknown'
            };
        } catch (reportError) {
            return {
                message: 'Failed to create error report',
                originalError: error.toString(),
                reportError: reportError.toString(),
                timestamp: new Date().toISOString()
            };
        }
    }

    /**
     * Safe DOM element inspection
     * @param {HTMLElement} element DOM element to inspect
     * @returns {Object} Element information
     */
    static inspectElement(element) {
        try {
            if (!element) {
                return { error: 'Element is null or undefined' };
            }

            return {
                tagName: element.tagName?.toLowerCase() || 'unknown',
                id: element.id || 'no-id',
                className: element.className || 'no-class',
                textContent: element.textContent?.trim().substring(0, 100) || 'no-text',
                attributes: Array.from(element.attributes || []).reduce((acc, attr) => {
                    acc[attr.name] = attr.value;
                    return acc;
                }, {}),
                dataset: element.dataset || {},
                hasParent: !!element.parentElement,
                childCount: element.children?.length || 0
            };
        } catch (error) {
            return {
                error: 'Failed to inspect element',
                message: error.message
            };
        }
    }
}

export default DebugHelper;